import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, AlertTriangle, ShieldCheck, Loader2 } from "lucide-react";

/** Admin-only tool: bulk-delete test events by title pattern with a dry-run preview. */
export default function PurgeTestsCard() {
  const [titlesRaw, setTitlesRaw] = useState("");
  const [citiesRaw, setCitiesRaw] = useState("");
  const [includeDefault, setIncludeDefault] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const parseList = (raw) => raw.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);

  const runDryRun = async () => {
    setErr("");
    setBusy(true);
    setPreview(null);
    try {
      const { data } = await api.post("/admin/events/purge-tests", {
        dry_run: true,
        include_default_pattern: includeDefault,
        title_exact: parseList(titlesRaw),
        test_cities: parseList(citiesRaw),
      });
      setPreview(data);
      if (data.would_delete === 0) toast.info("No matching test events found");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const runDelete = async () => {
    if (!preview || preview.would_delete === 0) return;
    if (!window.confirm(`Delete ${preview.would_delete} event(s)? This also removes their applications, notifications, ratings, and payment records. Cannot be undone.`)) return;
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/admin/events/purge-tests", {
        dry_run: false,
        include_default_pattern: includeDefault,
        title_exact: parseList(titlesRaw),
        test_cities: parseList(citiesRaw),
      });
      toast.success(`Deleted ${data.deleted} event(s) + ${data.applications_deleted} application(s)`);
      setPreview({ ...data, would_delete: 0 });
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="purge-tests-card"
      className="rounded-lg border p-6"
      style={{
        backgroundColor: "#fff",
        borderColor: "rgba(31,93,52,0.25)",
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="p-2 rounded-md shrink-0"
          style={{ backgroundColor: "rgba(31,93,52,0.12)", color: "var(--terracotta)" }}
        >
          <Trash2 size={18} />
        </div>
        <div>
          <div className="overline" style={{ color: "var(--ink-soft)" }}>Danger zone</div>
          <div className="font-serif text-2xl mt-1">Purge test events.</div>
          <p className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>
            Bulk-delete leftover test events from the database. Always runs a{" "}
            <strong>dry-run preview first</strong> — nothing is deleted until you click Delete.
            Cascades applications, notifications, ratings, and payment records.
          </p>
        </div>
      </div>

      <div className="mt-6 space-y-5">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            data-testid="purge-include-default"
            checked={includeDefault}
            onChange={(e) => setIncludeDefault(e.target.checked)}
            className="h-4 w-4"
          />
          <span>
            Match well-known test patterns (<code className="text-xs">TEST_</code>,{" "}
            <code className="text-xs">UI_TEST_</code>, <code className="text-xs">WL Test</code>,{" "}
            <code className="text-xs">E2E</code>, <code className="text-xs">SMOKE</code>,{" "}
            <code className="text-xs">PYTEST</code>, <code className="text-xs">FIXTURE</code>)
          </span>
        </label>

        <div>
          <Label htmlFor="purge-titles" className="text-xs uppercase tracking-widest">
            Extra titles to match (one per line, case-insensitive, exact match)
          </Label>
          <textarea
            id="purge-titles"
            data-testid="purge-titles"
            rows={3}
            value={titlesRaw}
            onChange={(e) => setTitlesRaw(e.target.value)}
            placeholder="AdminFair&#10;Fall Makers Market"
            className="mt-2 w-full rounded-md border px-3 py-2 text-sm font-mono"
            style={{ borderColor: "rgba(0,0,0,0.15)", backgroundColor: "#fbf9f5" }}
          />
        </div>

        <div>
          <Label htmlFor="purge-cities" className="text-xs uppercase tracking-widest">
            Test cities (also matches any city containing "test")
          </Label>
          <Input
            id="purge-cities"
            data-testid="purge-cities"
            value={citiesRaw}
            onChange={(e) => setCitiesRaw(e.target.value)}
            placeholder="TestCity, UITestCity2"
            className="mt-2"
          />
        </div>

        {err && (
          <div
            data-testid="purge-error"
            className="flex items-start gap-2 text-sm rounded-md p-3"
            style={{ backgroundColor: "rgba(31,93,52,0.08)", color: "var(--terracotta)" }}
          >
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <div>{err}</div>
          </div>
        )}

        <div className="flex gap-3 flex-wrap">
          <Button
            type="button"
            onClick={runDryRun}
            disabled={busy}
            data-testid="purge-preview"
            variant="outline"
            className="rounded-full inline-flex items-center gap-2"
          >
            {busy && !preview ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
            Preview matches
          </Button>
          {preview && preview.would_delete > 0 && (
            <Button
              type="button"
              onClick={runDelete}
              disabled={busy}
              data-testid="purge-confirm"
              className="rounded-full inline-flex items-center gap-2"
              style={{ backgroundColor: "var(--terracotta)", color: "#fff" }}
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete {preview.would_delete} event{preview.would_delete === 1 ? "" : "s"}
            </Button>
          )}
        </div>

        {preview && (
          <div
            data-testid="purge-preview-list"
            className="rounded-md border p-4"
            style={{
              backgroundColor: "rgba(0,0,0,0.03)",
              borderColor: "rgba(0,0,0,0.08)",
            }}
          >
            <div
              className="text-xs uppercase tracking-widest mb-3"
              style={{ color: "var(--ink-soft)" }}
            >
              {preview.dry_run
                ? `Would delete ${preview.would_delete} event(s)`
                : `Deleted ${preview.deleted} event(s)`}
            </div>
            {preview.matches && preview.matches.length > 0 ? (
              <ul className="space-y-2 text-sm">
                {preview.matches.map((m) => (
                  <li key={m.id} className="flex items-baseline gap-2">
                    <span
                      className="text-[10px] font-mono px-1.5 py-0.5 rounded"
                      style={{
                        backgroundColor: "rgba(31,93,52,0.12)",
                        color: "var(--terracotta)",
                      }}
                    >
                      {m.moderation_status}
                    </span>
                    <span className="font-medium">{m.title}</span>
                    <span className="text-xs" style={{ color: "var(--ink-soft)" }}>
                      · {m.city} · {m.date}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm" style={{ color: "var(--ink-soft)" }}>
                No matching events.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
